import { LabeledValue, ModalOpenButton } from "@msys/ui";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import { Box, Button, IconButton } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { CategoriesSearchModal } from "./CategoriesSearchModal.js";
import { CategoriesLabeledValue } from "./CategoriesLabeledValue.js";
import type { CategoryFragment } from "./useCategories.generated.js";

interface Props {
  categoryIds: string[];
  label?: string;
  selectButtonLabel?: string;
  leftIcon?: React.ReactNode;
  rightButton?: React.ReactNode;
  onChange: (categories: CategoryFragment[]) => Promise<void> | void;
  onClear?: () => Promise<void> | void;
  required?: boolean;
  disabled?: boolean;
  canEdit?: boolean;
  alwaysShowLabel?: boolean;
  addParentsWhenNotIncluded?: boolean;
  removeChildrenIfIncluded?: boolean;
}

export const CategoriesInput = ({
  categoryIds,
  label,
  selectButtonLabel,
  leftIcon,
  rightButton,
  required = false,
  disabled = false,
  canEdit = true,
  alwaysShowLabel = false,
  onChange,
  onClear,
  addParentsWhenNotIncluded,
  removeChildrenIfIncluded,
}: Props) => {
  const { t } = useTranslate(["Categories", "Global"]);
  if (!canEdit) {
    return (
      <CategoriesLabeledValue
        categoryIds={categoryIds}
        label={label}
        leftIcon={leftIcon}
        addParentsWhenNotIncluded={addParentsWhenNotIncluded}
        removeChildrenIfIncluded={removeChildrenIfIncluded}
      />
    );
  }
  if (categoryIds.length === 0) {
    const button = (
      <ModalOpenButton
        Modal={CategoriesSearchModal}
        modalProps={{
          handleSelectCategories: async (categories, handleClose) => {
            await onChange(categories);
            handleClose();
          },
        }}
        disabled={disabled}
      >
        <Button
          size="small"
          color="secondary"
          variant="text"
          startIcon={<AddIcon />}
          disabled={disabled}
          sx={{ textWrap: "nowrap" }}
        >
          {selectButtonLabel ?? t("Add categories", { ns: "Categories" })}
        </Button>
      </ModalOpenButton>
    );
    return alwaysShowLabel ? (
      <Box display="flex" alignItems="center" minHeight="48px">
        <LabeledValue label={label ?? ""}>{button}</LabeledValue>
      </Box>
    ) : (
      <Box display="flex">{button}</Box>
    );
  }

  return (
    <CategoriesLabeledValue
      categoryIds={categoryIds}
      label={label}
      leftIcon={leftIcon}
      required={required}
      rightButton={
        rightButton ?? (
          <ModalOpenButton
            Modal={CategoriesSearchModal}
            modalProps={{
              initialSelectedCategoryIds: categoryIds,
              handleSelectCategories: async (categories, handleClose) => {
                await onChange(categories);
                handleClose();
              },
              handleClearCategories: onClear
                ? async handleClose => {
                    await onClear();
                    handleClose();
                  }
                : undefined,
            }}
            disabled={disabled}
          >
            <IconButton size="small" color="primary" disabled={disabled}>
              <EditIcon />
            </IconButton>
          </ModalOpenButton>
        )
      }
      addParentsWhenNotIncluded={addParentsWhenNotIncluded}
      removeChildrenIfIncluded={removeChildrenIfIncluded}
    />
  );
};
